import { FC, useState, useEffect } from 'react';
import { Box, TextField, FormControl, Fade, Grid, FormControlLabel, Checkbox } from '@mui/material';
import { Subject, UploadFile } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { ImageModal, Loader, ModalSaveSection, Modal, Card, Toast } from '../../../../components';
import { DatePicker } from '@mui/x-date-pickers';
import { ITodoInfo } from '../../../../models';
import { formatDBFriendlyDateTime } from '../../../../helpers';
import { getTodoItemInfo, createTodoItem, createTodoImages } from '../../../../fetch';
import { TodoUpload } from './todo-upload';
import { addDays } from 'date-fns';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

interface IAddTodoModal {
  handleClose: (shouldRefresh?: boolean, shouldClose?: boolean) => void;
  open: boolean;
  buildId?: number | string;
}

export const AddTodoModal: FC<IAddTodoModal> = ({ open, handleClose, buildId }) => {
  const classes = useStyles();
  const [isLoadingTodoItemInfo, setIsLoadingTodoItemInfo] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [todoItem, setTodoItem] = useState<ITodoInfo>();
  const [selectedDueDate, setSelectedDueDate] = useState<Date | string>('');
  const [todoFiles, setTodoFiles] = useState<File[]>([]);
  const [todoDescription, setTodoDescription] = useState('');
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [sendEmail, setSendEmail] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  const fetchTodoItemInfo = async (showLoader?: boolean, setTodo: boolean = true) => {
    try {
      if (showLoader) {
        setIsLoadingTodoItemInfo(true);
      }
      const res = await getTodoItemInfo(buildId);
      setTodoItem(res.data);
      setSelectedDueDate(addDays(new Date(), 7));
    } catch (error) {
      enqueueSnackbar(error.response.data?.Detail, { variant: 'error' });
    } finally {
      setIsLoadingTodoItemInfo(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchTodoItemInfo(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildId, open]);

  const handleTodoFileUploads = async (): Promise<string[]> => {
    if (todoFiles?.length > 0) {
      try {
        const formData = new FormData();
        todoFiles.forEach(file => {
          formData.append('images', file);
        });
        return await createTodoImages(formData);
      } catch (error) {
        enqueueSnackbar(error.response.data?.Detail, { variant: 'error' });
      } finally {
        setTodoFiles([]);
        setIsDirty(false);
      }
    }
  };

  const clearState = async (saveAndContinue = false) => {
    setTodoItem(null);
    setIsDirty(false);
    setTodoDescription('');
    setSelectedDueDate(null);
    setSendEmail(true);

    if (!saveAndContinue) {
      await fetchTodoItemInfo(true, false);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      const result = window.confirm('You have unsaved changes, are you sure you want to exit?');
      if (result) {
        handleClose();
      } else {
        return;
      }
    } else {
      handleClose();
    }
    clearState();
  };

  const descriptionRows =
    todoDescription.length > 430
      ? 6
      : todoDescription.length > 325
      ? 5
      : todoDescription.length > 225
      ? 4
      : todoDescription.length > 125
      ? 3
      : 2;

  return (
    <Modal open={open} onClose={handleCancel} maxWidth="md" title="Add Todo">
      <Fade in={open}>
        <div>
          <Card title="Details" icon={<Subject />}>
            <Box mt={1}>
              {(isLoadingTodoItemInfo || isSaving) && <Loader position="centered" type="overlay" />}
              <Grid container spacing={1}>
                <Grid
                  item
                  xs={12}
                  justifyContent="space-between"
                  alignItems="center"
                  display="flex"
                  flexDirection="row"
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendEmail}
                        id="send-email-checkbox"
                        onChange={() => {
                          setSendEmail(!sendEmail);
                        }}
                      />
                    }
                    label="Send Email"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    variant="outlined"
                    autoComplete="nope"
                    label="Punchlist"
                    name="Punchlist"
                    disabled={true}
                    value={todoItem?.punchlist?.name || ''}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    autoComplete="nope"
                    label="Assign To"
                    name="assignTo"
                    disabled={true}
                    value={todoItem?.supervisor || ''}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    variant="outlined"
                    autoComplete="nope"
                    label="Description"
                    name="description"
                    multiline
                    disabled={isLoadingTodoItemInfo}
                    rows={descriptionRows}
                    value={todoDescription}
                    size="small"
                    className={classes.resizableTextarea}
                    onChange={e => {
                      if (e.target.value.length > 500) {
                        setError(true);
                      } else {
                        setIsDirty(true);
                        setTodoDescription(e.target.value);
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl required fullWidth variant="outlined">
                    <DatePicker
                      label="Due Date"
                      value={selectedDueDate}
                      disabled={isLoadingTodoItemInfo}
                      onChange={date => {
                        if (date === null) {
                          setSelectedDueDate(null);
                          return;
                        }
                        setSelectedDueDate(date);
                        setIsDirty(true);
                      }}
                      slotProps={{
                        textField: {
                          size: 'small',
                          error: false
                        }
                      }}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </Card>
          <Card title="Uploads" icon={<UploadFile />}>
            <TodoUpload
              files={todoFiles}
              uploadText="Attach file(s) to this item"
              setFiles={files => {
                setTodoFiles(files);
                if (files?.length > 0) {
                  setIsDirty(true);
                } else {
                  setIsDirty(false);
                }
              }}
            />
          </Card>
          <ModalSaveSection
            handleCancel={handleCancel}
            isSaveDisabled={!todoDescription || !selectedDueDate || isSaving || error}
            handleSave={async (saveAndContinue = false) => {
              try {
                setIsSaving(true);
                // creating a new todo item
                const guids = await handleTodoFileUploads();
                await createTodoItem(buildId, {
                  description: todoDescription,
                  photoGuids: guids ?? [],
                  dueDate: formatDBFriendlyDateTime(selectedDueDate),
                  shouldSendCreationEmail: sendEmail
                });
                enqueueSnackbar(`To-do added!`, {
                  variant: 'success'
                });
                if (!saveAndContinue) {
                  handleClose(true);
                } else {
                  handleClose(true, false);
                }
                await clearState(saveAndContinue);
              } catch (error) {
                enqueueSnackbar(error?.Detail ?? 'Error saving item, please try again.', {
                  variant: 'error'
                });
              } finally {
                setIsSaving(false);
              }
            }}
          />
        </div>
      </Fade>
      <Toast
        id="description-error"
        autoHideDuration={5000}
        message={'Description cannot exceed 500 characters.'}
        open={error}
        onClose={() => setError(false)}
        variant="error"
      />
      <ImageModal
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage('')}
        source={selectedImage}
      />
    </Modal>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  resizableTextarea: {
    '& textarea': {
      resize: 'vertical',
      overflow: 'auto'
    }
  }
}));
